import { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Button, message, Spin, Row, Col, DatePicker, Select, Switch,
} from 'antd';
import dayjs from 'dayjs';
 
import { smilepassportApi } from '../../../api/smilepassport.api';
 
const DEFAULT_TERMS = [
  { term_no: 1, term_name: 'Term 1', startMonth: 'January', endMonth: 'March' },
  { term_no: 2, term_name: 'Term 2', startMonth: 'April', endMonth: 'June' },
  { term_no: 3, term_name: 'Term 3', startMonth: 'July', endMonth: 'September' },
  { term_no: 4, term_name: 'Term 4', startMonth: 'October', endMonth: 'December' },
];
 
const MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
].map((month) => ({ value: month, label: month }));

const CONCERN_OPTIONS = [
  'Teeth Alignment',
  'Cavities',
  'Pain',
  'Gum Bleeding',
  'Teeth Discoloration',
  'Broken Teeth',
  'Speech Difficulty',
  'Mouth Breathing',
  'Snoring',
].map((concern) => ({ value: concern, label: concern }));
 
const StatusToggle = ({ value, onChange }) => (
  <Switch
    checked={value === 'published'}
    onChange={(checked) => onChange(checked ? 'published' : 'draft')}
    checkedChildren="Published"
    unCheckedChildren="Draft"
    style={{ minWidth: 100 }}
  />
);
 
const SmilepassportModal = ({
  open,
  onClose,
  eventId,
  childId,
  childName,
  onSuccess,
  editingRecord = null,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
 
  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
 
    const defaultStatus = editingRecord?.status || 'draft';
    form.setFieldsValue({
      concern: editingRecord?.concern,
      nasal_breathing_completed: editingRecord?.nasal_breathing_completed,
      tooth_brushing_completed: editingRecord?.tooth_brushing_completed,
      academic_year: editingRecord?.academic_year,
      terms: editingRecord?.terms?.length ? editingRecord.terms : DEFAULT_TERMS,
      next_checkup: editingRecord?.next_checkup ? dayjs(editingRecord.next_checkup) : undefined,
      status: defaultStatus,
    });
  }, [open, editingRecord, form]);
 
  const handleSubmit = async (rawValues) => {
    try {
      setLoading(true);
 
      const values = {
        ...rawValues,
        terms: (rawValues.terms || []).map((term, index) => ({ ...term, term_no: index + 1 })),
        next_checkup: rawValues.next_checkup ? rawValues.next_checkup.toISOString() : undefined,
      };
 
      if (editingRecord) {
        await smilepassportApi.update(editingRecord.id || editingRecord._id, values);
        message.success('Smilepassport record updated successfully');
      } else {
        await smilepassportApi.create({
          event: eventId,
          child: childId,
          ...values,
        });
        message.success('Smilepassport record created successfully');
      }
 
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      message.error(error.message || 'Failed to save smilepassport record');
    } finally {
      setLoading(false);
    }
  };
 
  const handleDelete = async () => {
    if (!editingRecord) return;
    try {
      setLoading(true);
      await smilepassportApi.delete(editingRecord.id || editingRecord._id);
      message.success('Smilepassport record deleted successfully');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      message.error(error.message || 'Failed to delete smilepassport record');
    } finally {
      setLoading(false);
    }
  };
 
  return (
    <Modal
      title={`Smile Passport - ${childName}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          {/* Academic Year */}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item label="Academic Year" name="academic_year">
                <Input placeholder="e.g., 2024-2025" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Next Checkup" name="next_checkup">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Select next checkup date" />
              </Form.Item>
            </Col>
          </Row>
 
          {/* Clinical notes */}
          <div style={{ padding: 16, background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 12, marginBottom: 16 }}>
            <Form.Item label="Concern" name="concern">
              <Select placeholder="Select concern" options={CONCERN_OPTIONS} allowClear />
            </Form.Item>
          </div>
 
          {/* Term-wise checkups */}
          {DEFAULT_TERMS.map((defaultTerm, index) => (
            <div
              key={defaultTerm.term_no}
              style={{ padding: 16, background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 12, marginBottom: 16 }}
            >
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Term {index + 1}</div>
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item label="Term Name" name={['terms', index, 'term_name']}>
                    <Input placeholder={`e.g., ${defaultTerm.term_name}`} />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8}>
                  <Form.Item label="Start Month" name={['terms', index, 'startMonth']}>
                    <Select placeholder={`e.g., ${defaultTerm.startMonth}`} options={MONTH_OPTIONS} allowClear />
                  </Form.Item>
                </Col>
                <Col xs={12} sm={8}>
                  <Form.Item label="End Month" name={['terms', index, 'endMonth']}>
                    <Select placeholder={`e.g., ${defaultTerm.endMonth}`} options={MONTH_OPTIONS} allowClear />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item label="Airway" name={['terms', index, 'airway']} rules={[{ max: 70, message: 'Airway must be at most 70 characters' }]}>
                    <Input.TextArea rows={3} maxLength={70} showCount placeholder="Airway observations" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label="Sleep Status" name={['terms', index, 'sleep_status']} rules={[{ max: 70, message: 'Sleep Status must be at most 70 characters' }]}>
                    <Input.TextArea rows={3} maxLength={70} showCount placeholder="Sleep pattern notes" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item label="Alignment" name={['terms', index, 'alignment']} rules={[{ max: 70, message: 'Alignment must be at most 70 characters' }]}>
                    <Input.TextArea rows={3} maxLength={70} showCount placeholder="Teeth / jaw alignment details" />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          ))}
 
          {/* Day counts */}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Nasal Breathing Days"
                name="nasal_breathing_completed"
              >
                <Input.TextArea rows={3} placeholder="Nasal Breathing Days" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Tooth Brushing Days"
                name="tooth_brushing_completed"
               
              >
                <Input.TextArea rows={3} placeholder="Tooth Brushing Days" />
              </Form.Item>
            </Col>
          </Row>
 
          {/* Status toggle — last field */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginBottom: 24 }}>
            <span style={{ fontWeight: 600 }}>Status</span>
            <Form.Item name="status" noStyle>
              <StatusToggle />
            </Form.Item>
          </div>
 
          {/* Footer */}
          <Row gutter={8} justify="end" style={{ marginTop: 8 }}>
            {editingRecord && (
              <Col>
                {/* <Button danger onClick={handleDelete} loading={loading} disabled={loading}>
                  Delete
                </Button> */}
              </Col>
            )}
            <Col>
              <Button onClick={onClose} disabled={loading}>Cancel</Button>
            </Col>
            <Col>
              <Button type="primary" htmlType="submit" loading={loading} disabled={loading}>
                {editingRecord ? 'Update' : 'Create'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};
 
export default SmilepassportModal;
 
